/**
 * Month End Picker Field Component
 *
 * A single-date picker for selecting a month-end date ("As of").
 * Provides last 12 months as presets plus a custom month picker.
 */

import React, { useState, useRef } from 'react'
import {
  Box,
  TextField,
  Button,
  Popover,
  Typography,
  Divider,
  Stack,
  InputAdornment,
  IconButton,
} from '@mui/material'
import { CalendarMonth, Clear } from '@mui/icons-material'
import {
  endOfMonth,
  subMonths,
  format,
  parse,
  isValid,
} from 'date-fns'
import { poolwerxColors } from '../../../../theme/colors'

interface MonthEndPickerFieldProps {
  endDate: string
  onChange: (endDate: string) => void
  disabled?: boolean
}

const formatDateForInput = (date: Date): string => {
  return format(date, 'yyyy-MM-dd')
}

const formatDateForDisplay = (dateStr: string): string => {
  if (!dateStr) return ''
  try {
    const date = parse(dateStr, 'yyyy-MM-dd', new Date())
    if (!isValid(date)) return dateStr
    return format(date, 'MMM yyyy')
  } catch {
    return dateStr
  }
}

const getMonthPresets = (): { label: string; endDate: Date }[] => {
  const today = new Date()
  return Array.from({ length: 12 }, (_, i) => {
    const monthDate = subMonths(today, i + 1)
    return {
      label: format(monthDate, 'MMM yyyy'),
      endDate: endOfMonth(monthDate),
    }
  })
}

const MonthEndPickerField: React.FC<MonthEndPickerFieldProps> = ({
  endDate,
  onChange,
  disabled = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [customMonth, setCustomMonth] = useState('')
  const inputRef = useRef<HTMLDivElement>(null)

  const open = Boolean(anchorEl)

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (disabled) return
    setCustomMonth('')
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handlePresetClick = (preset: { endDate: Date }) => {
    onChange(formatDateForInput(preset.endDate))
    handleClose()
  }

  const handleCustomApply = () => {
    if (!customMonth) return
    const parsed = parse(customMonth, 'yyyy-MM', new Date())
    if (isValid(parsed)) {
      onChange(formatDateForInput(endOfMonth(parsed)))
      handleClose()
    }
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange('')
  }

  const displayValue = endDate ? formatDateForDisplay(endDate) : ''
  const presets = getMonthPresets()

  return (
    <Box>
      <Typography
        component="label"
        sx={{
          fontWeight: 600,
          color: 'text.secondary',
          fontSize: '0.75rem',
          mb: 0.5,
          display: 'block',
        }}
      >
        As of
      </Typography>
      <TextField
        ref={inputRef}
        fullWidth
        size="small"
        placeholder="Select month"
        value={displayValue}
        onClick={handleOpen}
        disabled={disabled}
        InputProps={{
          readOnly: true,
          startAdornment: (
            <InputAdornment position="start">
              <CalendarMonth sx={{ color: 'action.active', fontSize: 20 }} />
            </InputAdornment>
          ),
          endAdornment: displayValue && !disabled ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={handleClear}
                sx={{ p: 0.5 }}
              >
                <Clear sx={{ fontSize: 18 }} />
              </IconButton>
            </InputAdornment>
          ) : null,
          sx: {
            cursor: disabled ? 'default' : 'pointer',
            '& input': { cursor: disabled ? 'default' : 'pointer' },
          },
        }}
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: { mt: 1, minWidth: 320 },
          },
        }}
      >
        <Box sx={{ display: 'flex' }}>
          {/* Presets sidebar */}
          <Box
            sx={{
              width: 140,
              borderRight: 1,
              borderColor: 'divider',
              py: 1,
              maxHeight: 320,
              overflowY: 'auto',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                px: 2,
                py: 1,
                display: 'block',
                color: 'text.secondary',
                fontWeight: 600,
              }}
            >
              Month End
            </Typography>
            {presets.map((preset) => (
              <Button
                key={preset.label}
                fullWidth
                size="small"
                onClick={() => handlePresetClick(preset)}
                sx={{
                  justifyContent: 'flex-start',
                  px: 2,
                  py: 0.75,
                  textTransform: 'none',
                  color: 'text.primary',
                  borderRadius: 0,
                  '&:hover': {
                    backgroundColor: poolwerxColors.tealLight,
                  },
                }}
              >
                {preset.label}
              </Button>
            ))}
          </Box>

          {/* Custom month input */}
          <Box sx={{ p: 2, flex: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{ mb: 2, color: 'text.secondary' }}
            >
              Custom Month
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="month"
              value={customMonth}
              onChange={(e) => setCustomMonth(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Divider sx={{ my: 2 }} />
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button
                size="small"
                onClick={handleClose}
                sx={{ textTransform: 'none' }}
              >
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={handleCustomApply}
                disabled={!customMonth}
                sx={{
                  textTransform: 'none',
                  backgroundColor: poolwerxColors.teal,
                  '&:hover': {
                    backgroundColor: poolwerxColors.tealHover,
                  },
                }}
              >
                Apply
              </Button>
            </Stack>
          </Box>
        </Box>
      </Popover>
    </Box>
  )
}

export default MonthEndPickerField
